<script setup>
import { ref, computed } from 'vue';

import microsoftClient from 'dashboard/api/channel/microsoftClient';
import googleClient from 'dashboard/api/channel/googleClient';
import SettingsSubPageHeader from '../../../SettingsSubPageHeader.vue';

import { useAlert } from 'dashboard/composables';
import { useI18n } from 'dashboard/composables/useI18n';

const props = defineProps({
  provider: {
    type: String,
    required: true,
    validate: value => ['microsoft', 'google'].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  submitButtonText: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    required: true,
  },
  inputPlaceholder: {
    type: String,
    required: true,
  },
});

const isRequestingAuthorization = ref(false);
const email = ref('');

const { t: $t } = useI18n();

const client = computed(() => {
  if (props.provider === 'microsoft') {
    return microsoftClient;
  }

  return googleClient;
});

async function requestAuthorization() {
  try {
    isRequestingAuthorization.value = true;
    const response = await client.value.generateAuthorization({
      email: email.value,
    });
    const {
      data: { url },
    } = response;

    window.location.href = url;
  } catch (error) {
    useAlert(props.errorMessage);
  } finally {
    isRequestingAuthorization.value = false;
  }
}
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0"
  >
    <SettingsSubPageHeader
      :header-title="title"
      :header-content="description"
    />

    <!-- Google-specific permission guidance -->
    <div
      v-if="provider === 'google'"
      class="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md"
    >
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="ml-3">
          <h4 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            {{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_REQUIRED_WARNING') }}
          </h4>
          <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
            <p>
              {{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_ERROR.INSTRUCTION') }}
            </p>
            <ul class="mt-2 list-disc list-inside space-y-1">
              <li>✅ {{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_ERROR.SCOPE_DESCRIPTIONS.PROFILE') }}</li>
              <li>✅ {{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_ERROR.SCOPE_DESCRIPTIONS.EMAIL') }}</li>
              <li>✅ <strong>{{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_ERROR.SCOPE_DESCRIPTIONS.GMAIL') }}</strong> {{ $t('INBOX_MGMT.ADD.GOOGLE.PERMISSIONS_ERROR.SCOPE_DESCRIPTIONS.GMAIL_ESSENTIAL') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <form class="mt-6" @submit.prevent="requestAuthorization">
      <woot-input
        v-model="email"
        type="email"
        :placeholder="inputPlaceholder"
      />
      <woot-submit-button
        icon="brand-twitter"
        type="submit"
        :button-text="submitButtonText"
        :loading="isRequestingAuthorization"
      />
    </form>
  </div>
</template>
